'use client';
import Link from 'next/link';
import { Icons } from '@/components/elements/Icon/constants';
import Icon from '@/components/elements/Icon';
import { useTranslations } from 'use-intl';
import { useState } from 'react';
import { SidebarProps } from './types';
import { footerLinks, iconMap } from './constants';
import { useNetwork } from '@/presentation/context/network';

export const SidebarVertical = ({ activePage }: SidebarProps) => {
  const [showCreate, setShowCreate] = useState(false);
  const t = useTranslations('Sidebar');
  const { slug: chain } = useNetwork();

  return (
    <div className="hidden md:flex overflow-hidden hover:overflow-visible bg-gray-50 p-1.5 flex-col flex-1 justify-center w-12 md:w-16 group/sidebar hover:w-44 hover:shadow-xl transition-[width] duration-75">
      <nav>
        <ul className="space-x-0 space-y-2 block">
          {(['profile', 'collections', 'swap', 'auctions'] as const).map(
            (page) => (
              <li key={page}>
                <Link
                  href={`/${chain}/${page.toLowerCase()}`}
                  className={`${
                    page === activePage ? 'bg-gray-200' : 'hover:bg-gray-100'
                  } p-2 md:py-3 md:px-4 text-gray-800 min-w-[1rem] font-semibold flex min-h-[2.5rem] md:min-h-[3rem] items-center space-x-4 rounded-lg capitalize group transition-colors`}
                >
                  <Icon
                    name={iconMap[page]}
                    size={20}
                    className={`flex-none min-w-0 transition-colors
                      ${
                        page === activePage
                          ? 'text-orange-500'
                          : 'text-gray-400 group-hover:text-gray-500'
                      }
                    `}
                  />
                  <span className="inline min-w-[8ch] opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-75">
                    {t(page)}
                  </span>
                </Link>
              </li>
            )
          )}
          <li>
            <button
              type="button"
              onClick={() => setShowCreate((x) => !x)}
              className={`${
                'create' === activePage ? 'bg-gray-200' : 'hover:bg-gray-100'
              } py-2 px-1.5 max-w-full w-full md:py-3 md:px-3 text-gray-800 min-w-[1rem] font-semibold flex min-h-[2.5rem] md:min-h-[3rem] items-center space-x-4 rounded-lg capitalize group transition-colors`}
            >
              <Icon
                name={iconMap.create}
                size={24}
                className={`flex-none min-w-0 transition-colors
                    ${
                      'create' === activePage
                        ? 'text-orange-500'
                        : 'text-pink-500 group-hover:text-pink-600'
                    }
                  `}
              />
              <span className="inline text-left min-w-[8ch] opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-75">
                {t('create')}
              </span>
            </button>
            {(showCreate ||
              activePage === 'create' ||
              activePage === 'position' ||
              activePage === 'vault') && (
              <ul className="py-1 bg-gray-50 whitespace-nowrap pl-6 md:pl-8 block text-left opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-75 space-y-1">
                {/* <li className="ml-0.5 md:ml-0">
                  <Link
                    href={`/${chain}/create/vault`}
                    className={`${
                      activePage === 'vault'
                        ? 'bg-gray-200'
                        : 'bg-gray-50 hover:bg-gray-100 '
                    } py-2 px-5 text-base font-medium text-gray-800 block w-full text-left cursor-pointer rounded-lg min-w-[8ch]`}
                  >
                    {t('newVault')}
                  </Link>
                </li> */}
                <li className="ml-0.5 md:ml-0">
                  <Link
                    href={`/${chain}/create/position`}
                    className={`${
                      activePage === 'position'
                        ? 'bg-gray-200'
                        : 'bg-gray-50 hover:bg-gray-100 '
                    } py-2 px-5 text-base font-medium text-gray-800 block w-full text-left cursor-pointer rounded-lg min-w-[8ch]`}
                  >
                    {t('newPosition')}
                  </Link>
                </li>
              </ul>
            )}
          </li>
        </ul>
      </nav>
      <footer className="mt-auto pt-2">
        <ul className="space-y-1">
          <li>
            <Link
              href={`/${chain}/info`}
              className={`p-2 min-h-[2.5rem] md:min-h-[3rem] md:py-3 md:px-4 text-gray-800 font-semibold inline-flex md:flex items-center space-x-4 rounded-lg capitalize group transition-colors hover:bg-gray-100`}
            >
              <Icon
                name={Icons.FUNNEL}
                size={20}
                className={`flex-none block min-w-0 transition-colors text-gray-400`}
              />
              <span className="opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-75">
                {t('info')}
              </span>
            </Link>
          </li>
          {footerLinks.map((link) => (
            <li key={link.href}>
              <Link
                href={link.href}
                target="_blank"
                rel="noreferrer nofollow"
                className={`p-2 min-h-[2.5rem] md:min-h-[3rem] md:py-3 md:px-4 text-gray-800 font-semibold inline-flex md:flex items-center space-x-4 rounded-lg capitalize group transition-colors`}
              >
                <Icon
                  name={link.icon}
                  size={20}
                  className={`bg-gray-50 flex-none block min-w-0 transition-colors text-gray-400 ${link.iconHover}`}
                />
                <span className="opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-75">
                  {t(link.key)}
                </span>
                <Icon
                  name={Icons.EXTERNAL}
                  className="hidden md:block opacity-0 group-hover:opacity-50 transition-opacity flex-none"
                  size={16}
                />
              </Link>
            </li>
          ))}
        </ul>
      </footer>
    </div>
  );
};

export default SidebarVertical;
